import { VALID_NEXT_STATUSES, type OrderStatus } from '@/lib/orders-client';

export type BoardLaneId = 'carts' | 'new' | 'preparing' | 'en_route' | 'attention' | 'done' | 'cancelled';

/** Every lane id either layout can render — the collapsed-lanes preference is validated against it. */
export const ALL_BOARD_LANE_IDS: BoardLaneId[] = [
  'carts',
  'new',
  'preparing',
  'en_route',
  'attention',
  'done',
  'cancelled',
];

export interface BoardLane {
  id: BoardLaneId;
  /**
   * The column set this lane reads (`?status[]=`). Empty for the carts lane, which has its own
   * endpoint — drafts are not part of the placed statuses.
   */
  statuses: OrderStatus[];
  /**
   * The attention lane also reads the carts whose chat is waiting for a human (`?handoffCarts=true`):
   * a DRAFT the agent handed off mid-order is the operator's problem now, not the customer's.
   */
  handoffCarts?: boolean;
  /**
   * What dropping a card here means, most-preferred first: the first one the card's CURRENT status
   * actually allows wins. Empty = the lane takes no drops.
   */
  dropTargets: OrderStatus[];
  /** Header dot — semantic-token backgrounds don't carry lane identity, so these are explicit pairs. */
  accent: string;
  /**
   * How cards are ordered inside the lane. `urgency` = closest to late on top (a working queue);
   * `newest` = most recent first, which is what an archive should show — the order just completed
   * belongs at the top, not below the oldest row of the page.
   */
  rank: 'urgency' | 'newest';
  /**
   * The lane header's "what lands here" tooltip — `board.laneInfo.<infoKey>` in the orders
   * namespace. Defaults to the lane id; the POS board's done lane reads differently (sent to the POS
   * counts as completed there), so it names its own copy.
   */
  infoKey?: string;
  /**
   * The lane's title — `board.lanes.<labelKey>` in the orders namespace. Defaults to the lane id;
   * the POS board's `new` lane holds nothing but orders waiting on the customer's payment, so it
   * is titled after that rather than "new". Read through {@link laneLabelKey}.
   */
  labelKey?: string;
  /**
   * The lane is a queue of things that are STUCK, not a stage of the pipeline: it is shown only while
   * it holds something, and its cards glow. See {@link ATTENTION_LANE}.
   */
  showOnlyWhenNonEmpty?: boolean;
  /**
   * The lane reads nothing from the API: its cards are lifted out of the other lanes' answers
   * ("En camino", see `board-deliveries.ts`). Never handed to `useBoardLanes` — an empty `statuses`
   * there would read EVERY placed order.
   */
  derived?: boolean;
}

const CARTS_LANE: BoardLane = {
  id: 'carts',
  statuses: [],
  dropTargets: [],
  accent: 'bg-slate-400 dark:bg-slate-500',
  rank: 'urgency',
};

/**
 * The lane of things that are blocked at the door. Both of its populations sit BEFORE acceptance on
 * the order's timeline: a paid order whose POS push failed has not entered the pipeline yet (its
 * resolutions are "keyed into the POS by hand" → done, or cancel — never an acceptance queue in
 * Menteo: the POS owns acceptance on the brands that have one),
 * and a handoff cart has not even been placed. That is why it sits right after the carts and ahead
 * of "new" on both boards — and why it only appears while it has something in it: an empty column
 * of exceptions is noise, a non-empty one is the first thing the operator should see.
 */
const ATTENTION_LANE: BoardLane = {
  id: 'attention',
  statuses: ['pos_push_failed'],
  handoffCarts: true,
  // Nothing is ever moved INTO the failure lane by hand — the POS push (or the agent's handoff)
  // puts it there.
  dropTargets: [],
  accent: 'bg-red-500 dark:bg-red-400',
  rank: 'urgency',
  showOnlyWhenNonEmpty: true,
};

/**
 * The board of a brand WITHOUT a POS integration, in operator order. Every stage of the order's life
 * is worked here — accept, prepare, complete, cancel — so each one gets a column. "Preparing" also
 * spans the pushed statuses for the rare order that reached an external system anyway.
 */
const MANUAL_LANES: BoardLane[] = [
  CARTS_LANE,
  ATTENTION_LANE,
  {
    id: 'new',
    statuses: ['pending_payment', 'pending'],
    // A parked POS failure taken over manually lands back in the acceptance queue.
    dropTargets: ['pending'],
    accent: 'bg-yellow-500 dark:bg-yellow-400',
    rank: 'urgency',
  },
  {
    id: 'preparing',
    statuses: ['confirmed', 'pushed_to_external_pending', 'pushed_to_external'],
    dropTargets: ['confirmed', 'pushed_to_external'],
    accent: 'bg-blue-500 dark:bg-blue-400',
    rank: 'urgency',
  },
  {
    id: 'done',
    statuses: ['completed'],
    dropTargets: ['completed'],
    accent: 'bg-emerald-500 dark:bg-emerald-400',
    rank: 'newest',
  },
  {
    id: 'cancelled',
    statuses: ['cancelled'],
    dropTargets: ['cancelled'],
    accent: 'bg-muted-foreground',
    rank: 'newest',
  },
];

/**
 * The board of a brand WITH a POS integration. A placed order goes straight to the POS
 * (`pushed_to_external`, terminal for us — the store's own system runs acceptance, preparation and
 * cancellation from there), so "sent to POS" IS completed here and the preparing / cancelled columns
 * would only ever show statuses the operator manages somewhere else. What is left to work on this
 * board is the cart that is still being built, the order that never made it across — and the order
 * that has not left yet: an online-paid order sits in `pending_payment` until the customer pays and
 * is only pushed afterwards. Without a column for it, a placed order that the customer is still
 * paying would be on no lane at all until the push either lands it in done or fails it into
 * attention.
 *
 * There is deliberately no `pending` anywhere on this board. A POS brand never accepts an order in
 * Menteo: placement pushes it (or parks it in attention), payment pushes it, and a parked failure
 * is resolved by keying the order into the POS by hand — which IS "sent to POS", i.e. done.
 */
const POS_LANES: BoardLane[] = [
  CARTS_LANE,
  ATTENTION_LANE,
  {
    id: 'new',
    statuses: ['pending_payment'],
    // Payment, not the operator, moves a pending_payment order on — nothing is dropped here.
    dropTargets: [],
    accent: 'bg-yellow-500 dark:bg-yellow-400',
    rank: 'urgency',
    labelKey: 'awaitingPayment',
    infoKey: 'awaitingPayment',
  },
  {
    id: 'done',
    statuses: ['completed', 'pushed_to_external', 'pushed_to_external_pending'],
    // A parked POS failure keyed into the POS by hand is "sent" — the same resolution the detail
    // panel offers.
    dropTargets: ['pushed_to_external', 'completed'],
    accent: 'bg-emerald-500 dark:bg-emerald-400',
    rank: 'newest',
    infoKey: 'donePos',
  },
];

/**
 * The board of an aggregate session whose brands DISAGREE on POS: some push to a POS, some work their
 * orders by hand. Every column either layout has is needed, so this is the manual board's column set
 * — but a pushed order belongs to its POS whichever brand it came from, so the pushed statuses read
 * as completed here exactly like on {@link POS_LANES} instead of sitting in "preparing" as if someone
 * in Menteo still had to finish them.
 *
 * `new` takes no drops: the only move into it is a parked POS failure taken back as `pending`, which
 * is a manual-brand resolution — and on this board the failure is almost always a POS brand's, whose
 * orders are never accepted in Menteo. The detail panel still offers every resolution per order.
 */
const MIXED_LANES: BoardLane[] = [
  CARTS_LANE,
  ATTENTION_LANE,
  {
    id: 'new',
    statuses: ['pending_payment', 'pending'],
    dropTargets: [],
    accent: 'bg-yellow-500 dark:bg-yellow-400',
    rank: 'urgency',
  },
  {
    id: 'preparing',
    statuses: ['confirmed'],
    dropTargets: ['confirmed'],
    accent: 'bg-blue-500 dark:bg-blue-400',
    rank: 'urgency',
    infoKey: 'preparingMixed',
  },
  {
    id: 'done',
    statuses: ['completed', 'pushed_to_external', 'pushed_to_external_pending'],
    dropTargets: ['completed', 'pushed_to_external'],
    accent: 'bg-emerald-500 dark:bg-emerald-400',
    rank: 'newest',
    infoKey: 'donePos',
  },
  {
    id: 'cancelled',
    statuses: ['cancelled'],
    dropTargets: ['cancelled'],
    accent: 'bg-muted-foreground',
    rank: 'newest',
  },
];

/**
 * Which board to draw. A line session (or a single brand) is `pos` or `manual` by its own POS
 * integration; an aggregate session spanning several brands gets that same board when they all
 * agree, and `mixed` when they don't.
 */
export type BoardLayout = 'manual' | 'pos' | 'mixed';

/**
 * The statuses where the order's life is over as far as we are concerned: nothing is left to work,
 * so the promise we made has nothing left to chase and the card stops counting against it (see the
 * timing line in `OrderBoardCard`). `pushed_to_external` is one of them on purpose — once the order
 * is the POS's, the store's own system owns acceptance, preparation and delivery, exactly like a
 * `completed` order on a board without a POS (see {@link POS_LANES}).
 *
 * `pushed_to_external_pending` is NOT here: the push is still in flight, so the order has not
 * actually landed anywhere yet.
 */
const SETTLED_STATUSES: OrderStatus[] = ['completed', 'pushed_to_external', 'cancelled'];

/** See {@link SETTLED_STATUSES}. */
export function isSettledStatus(status: OrderStatus): boolean {
  return SETTLED_STATUSES.includes(status);
}

/** The lanes for this layout — see {@link MANUAL_LANES}, {@link POS_LANES} and {@link MIXED_LANES}. */
export function boardLanesFor(layout: BoardLayout): BoardLane[] {
  if (layout === 'pos') return POS_LANES;
  if (layout === 'mixed') return MIXED_LANES;
  return MANUAL_LANES;
}

/** The `orders` locale key of a lane's title — see {@link BoardLane.labelKey}. */
export function laneLabelKey(lane: BoardLane): string {
  return `board.lanes.${lane.labelKey ?? lane.id}`;
}

/**
 * The raw statuses a lane reads, for its header tooltip. `statuses` alone would leave two lanes
 * blank: the carts lane reads drafts off its own endpoint, and the attention lane unions in the
 * handoff drafts on top of its column set.
 */
export function laneStatusesFor(lane: BoardLane): OrderStatus[] {
  if (lane.id === 'carts') return ['draft'];
  return lane.handoffCarts ? [...lane.statuses, 'draft'] : lane.statuses;
}

/**
 * The lane a status belongs to on this board. Drafts answer `carts` (a handoff cart is ALSO on the
 * attention lane, which the stream handling covers separately); the retired-cart statuses
 * (`abandoned`, and `superseded` — the chat cart the customer's web cart won over, D17) belong to no
 * lane at all, so they never show up on the board or in its counts. On the POS board the statuses
 * without a column (pending, confirmed, cancelled) answer null the same way.
 */
export function laneIdForStatus(status: OrderStatus, lanes: BoardLane[]): BoardLaneId | null {
  if (status === 'draft') return 'carts';
  return lanes.find((lane) => lane.statuses.includes(status))?.id ?? null;
}

/**
 * What dropping a card carrying `current` onto `lane` would do — null when the move is not a valid
 * transition, which is what makes the drop bounce instead of firing a write the backend would 400.
 */
export function dropStatusFor(current: OrderStatus, lane: BoardLane): OrderStatus | null {
  if (lane.dropTargets.length === 0) return null;
  const allowed = VALID_NEXT_STATUSES[current] ?? [];
  return lane.dropTargets.find((status) => allowed.includes(status)) ?? null;
}

/**
 * The lanes a card can be moved to right now — the keyboard fallback for drag-and-drop.
 *
 * The card's own lane is left out even when a transition inside it exists (a POS order going
 * pushed_to_external_pending → pushed_to_external): "move to the column you are already in" reads as
 * a no-op, so that step is the card's advance action instead.
 */
export function moveTargetsFor(current: OrderStatus, lanes: BoardLane[]): { lane: BoardLane; status: OrderStatus }[] {
  const own = laneIdForStatus(current, lanes);
  // The carts lane is read-only: a cart is the customer's own live draft, and `draft → pending`
  // exists in the transition table only for the placement path (pricing, payment link, the
  // confirmation message). Placing it FOR them from a menu would skip all of that, so the menu
  // offers nothing here either — the card is not draggable for exactly the same reason.
  if (own === 'carts') return [];
  return lanes.flatMap((lane) => {
    if (lane.id === own) return [];
    const status = dropStatusFor(current, lane);
    return status ? [{ lane, status }] : [];
  });
}
